import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Badge, Modal, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import orderService from "../../services/orderService";
import deliveryProfileService from "../../services/deliveryProfileService";
import paymentService from "../../services/paymentService";
import InfoPill from "../Common/InfoPill";
import ConfirmModal from "../Common/ConfirmModal";
import { formatDateTimeToDDMMYYYY12Hr } from "../../utils/dateUtils";
import { formatQuantity, formatQuantityWithUnit } from "../../utils/quantityUtils";
import {
  FaFileInvoice,
  FaShoppingBag,
  FaHourglassHalf,
  FaCheckCircle,
  FaCoins,
  FaUndo,
  FaExchangeAlt,
} from "react-icons/fa";
import DateRangePicker from "rsuite/DateRangePicker";
import "rsuite/DateRangePicker/styles/index.css";

const AdminOrders = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth);
  const isVendor = user?.user_type === "vendor";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  // New state for delivery partner selection modal
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [orderToAccept, setOrderToAccept] = useState(null);
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [selectedDeliveryPartner, setSelectedDeliveryPartner] = useState("");
  const [deliveryPartnersLoading, setDeliveryPartnersLoading] = useState(false);

  // New state for cancellation reason modal
  const [showCancelReasonModal, setShowCancelReasonModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");

  // Filter states matching Product List UI
  const [orderStatusFilter, setOrderStatusFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [returnFilter, setReturnFilter] = useState("");

  // Fetch orders
  const fetchOrders = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      // Get order_status from URL query params if not provided in filters
      const orderStatus = filters.status || searchParams.get("order_status");
      const requestFilters = { ...filters };
      if (orderStatus) {
        requestFilters.status = orderStatus;
      }

      const response = await orderService.getAllOrders(requestFilters);
      const ordersData = response.data.orders || [];

      // Ensure orders are sorted by created_at DESC (newest first)
      // Backend should already do this, but we'll ensure it on frontend too
      const sortedOrders = [...ordersData].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // Descending order (newest first)
      });

      // Flatten landmark property for search filter
      // Add landmark as a top-level property so Filter component can search it
      const ordersWithFlattenedLandmark = sortedOrders.map((order) => ({
        ...order,
        landmark: order.shipping_address_details?.landmark || "",
      }));

      setOrders(ordersWithFlattenedLandmark);
    } catch (err) {
      setError("Failed to fetch orders");
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchDeliveryPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("order_status")]);

  // Fetch delivery partners
  const fetchDeliveryPartners = async () => {
    try {
      setDeliveryPartnersLoading(true);
      const response = await deliveryProfileService.getDeliveryProfiles({
        is_active: true,
        limit: 100,
      });
      setDeliveryPartners(response.data || []);
    } catch (err) {
      console.error("Error fetching delivery partners:", err);
      alert("Failed to fetch delivery partners");
    } finally {
      setDeliveryPartnersLoading(false);
    }
  };



  // Handle order acceptance with delivery partner selection
  const handleAcceptOrder = (orderId, orderNumber) => {
    setOrderToAccept({ id: orderId, number: orderNumber });
    setSelectedDeliveryPartner("");
    fetchDeliveryPartners();
    setShowDeliveryModal(true);
  };

  // Handle order cancellation with reason
  const handleCancelOrder = (orderId, orderNumber) => {
    setOrderToCancel({ id: orderId, number: orderNumber });
    setCancellationReason("");
    setShowCancelReasonModal(true);
  };

  // Confirm order acceptance with delivery partner
  const confirmAcceptOrder = async (partnerOverride) => {
    const partner = partnerOverride || selectedDeliveryPartner;
    if (orderToAccept && partner) {
      try {
        setActionLoading((prev) => ({ ...prev, [orderToAccept.id]: true }));
        if (partner === "manual") {
          await orderService.updateOrderStatus(orderToAccept.id, "accepted", {
            courier_partner: "manual",
            bypass_delivery_partner: true,
          });
          toast.success("Order accepted with Manual / Self Dispatch!");
        } else if (partner === "shadowfax" || partner === "xpressbees" || partner === "delhivery") {
          // Resolve full order details from state array to access shipping_address_details
          const fullOrder = orders.find(o => o.id === orderToAccept.id) || orderToAccept;

          // Resolve destination pincode
          const pincode = fullOrder.shipping_address_details?.pincode ||
            fullOrder.shipping_address?.pincode ||
            (fullOrder.shipping_address_details && fullOrder.shipping_address_details.address_line
              ? String(fullOrder.shipping_address_details.address_line).match(/\d{6}/)?.[0]
              : null);

          if (!pincode) {
            toast.error("Could not find a valid 6-digit delivery pincode for this order!");
            return;
          }

          if (partner === "shadowfax") {
            // Check complete order serviceability (customer delivery and all vendor pickups)
            const serviceRes = await orderService.checkOrderServiceability(orderToAccept.id);
            if (!serviceRes?.serviceable) {
              toast.error(serviceRes?.message || `Order is not serviceable by Shadowfax! Please select another delivery partner.`);
              return;
            }

            // Trigger Shadowfax 3PL booking directly
            const response = await orderService.bookShipment(orderToAccept.id, "shadowfax");
            toast.success(response.message || "Order accepted & Shadowfax shipment booked successfully!");
          } else if (partner === "xpressbees") {
            // Check complete order serviceability (customer delivery and all vendor pickups)
            const serviceRes = await orderService.checkXpressbeesOrderServiceability(orderToAccept.id);
            if (!serviceRes?.serviceable) {
              toast.error(serviceRes?.message || `Order is not serviceable by XpressBees! Please select another delivery partner.`);
              return;
            }

            // Trigger XpressBees 3PL booking directly
            const response = await orderService.bookShipment(orderToAccept.id, "xpressbees");
            toast.success(response.message || "Order accepted & XpressBees shipment booked successfully!");
          } else if (partner === "delhivery") {
            // Check complete order serviceability via Delhivery
            const serviceRes = await orderService.checkDelhiveryOrderServiceability(orderToAccept.id);
            if (!serviceRes?.serviceable) {
              toast.error(serviceRes?.message || `Order is not serviceable by Delhivery! Please select another delivery partner.`);
              return;
            }

            // Trigger Delhivery 3PL booking directly
            const response = await orderService.bookShipment(orderToAccept.id, "delhivery");
            toast.success(response.message || "Order accepted & Delhivery shipment booked successfully!");
          }
        }
        else {
          throw new Error("Delivery partner not Selected");
        }
        await fetchOrders(); // Refresh orders
        setShowDeliveryModal(false);
        setOrderToAccept(null);
        setSelectedDeliveryPartner("");
      } catch (err) {
        console.error("Error accepting order:", err);
        toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to accept order");
      } finally {
        setActionLoading((prev) => ({ ...prev, [orderToAccept.id]: false }));
      }
    } else {
      toast.error("Please select a delivery partner");
    }
  };

  // Confirm order cancellation with reason
  const confirmCancelOrder = async () => {
    if (orderToCancel && cancellationReason.trim()) {
      try {
        setActionLoading((prev) => ({ ...prev, [orderToCancel.id]: true }));
        await orderService.cancelOrder(orderToCancel.id, {
          reason: cancellationReason.trim(),
        });

        // Mark order as cancelled by current user to prevent duplicate socket toast
        if (window.markOrderCancelledByUser) {
          window.markOrderCancelledByUser(orderToCancel.id);
        }

        await fetchOrders(); // Refresh orders
        setShowCancelReasonModal(false);
        setOrderToCancel(null);
        setCancellationReason("");
        toast.success("Order cancelled successfully");
      } catch (err) {
        console.error("Error cancelling order:", err);
        toast.error("Failed to cancel order");
      } finally {
        setActionLoading((prev) => ({ ...prev, [orderToCancel.id]: false }));
      }
    } else {
      toast.error("Please provide a reason for cancellation");
    }
  };

  const cancelDeleteOrder = () => {
    setShowConfirmModal(false);
    setOrderToCancel(null);
  };

  // Cancel delivery partner selection
  const cancelDeliverySelection = () => {
    setShowDeliveryModal(false);
    setOrderToAccept(null);
    setSelectedDeliveryPartner("");
  };

  // Cancel cancellation reason modal
  const cancelCancellationReason = () => {
    setShowCancelReasonModal(false);
    setOrderToCancel(null);
    setCancellationReason("");
  };



  // Check if order is paid (eligible for invoice)
  const isOrderPaid = (order) => {
    const paidStatuses = ["paid", "captured", "completed"];
    return paidStatuses.includes(order.payment_status?.toLowerCase());
  };

  // Handle invoice preview/download
  const handleInvoicePreview = async (orderId) => {
    try {
      const blob = await paymentService.downloadInvoice(orderId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice opened in new tab");
    } catch (error) {
      console.error("Error previewing invoice:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to preview invoice";
      toast.error(errorMessage);
    }
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status) => {
    const key = status?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    switch (key) {
      case "placed":
        return "warning";
      case "accepted":
      case "confirmed":
        return "info";
      case "packed":
      case "out_for_delivery":
        return "primary";
      case "delivered":
        return "success";
      case "cancelled":
        return "danger";
      case "returned":
        return "secondary";
      case "refunded":
        return "warning";
      default:
        return "secondary";
    }
  };

  // Get payment status badge variant
  const getPaymentStatusBadgeVariant = (status) => {
    const key = status?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    switch (key) {
      case "paid":
      case "captured":
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "refunded":
      case "partially_refunded":
        return "warning";
      case "failed":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Format date with DD-MM-YYYY and 12hr time format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return formatDateTimeToDDMMYYYY12Hr(dateString);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  // Convert status strings like "out_for_delivery" to "Out For Delivery"
  const toTitleCase = (value) => {
    if (!value || typeof value !== "string") return "";
    return value
      .toLowerCase()
      .split(/[_\s]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Table headers
  const headers = [
    {
      prop: "order_no",
      title: "Order No",
      isFilterable: true,
      // isSortable: true,
      cell: (row) => (
        <div>
          <Link
            to={`/admin/orders/details/${row.id}`}
            className="text-decoration-none fw-bold text-primary d-block"
            style={{ cursor: "pointer" }}
          >
            {row?.order_no || `#${row?.id?.slice(-8) || "Unknown"}`}
          </Link>
          {row.awb_number ? (
            <Badge bg="secondary" className="mt-1 d-block" style={{ fontSize: "10px", fontWeight: "normal", width: "fit-content" }}>
              🚚 {row.courier_partner || "3PL"}: {row.awb_number}
            </Badge>
          ) : String(row.courier_partner).toLowerCase() === "manual" ? (
            <Badge bg="light" text="dark" className="mt-1 d-block border" style={{ fontSize: "10px", fontWeight: "normal", width: "fit-content" }}>
              📦 Manual Dispatch
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      prop: "customer_phone",
      title: "Customer",
      // isSortable: true,
      isFilterable: true,
      cell: (row) => {
        const customerName = row.shipping_address_details?.receiver_name || row.customer_name || "Unknown";
        const customerPhone = row.shipping_address_details?.receiver_phone || row.customer_phone || "-";
        return (
          <div>
            <div className="fw-semibold">{customerName}</div>
            <small className="text-muted">{customerPhone}</small>
          </div>
        );
      },
    },
    {
      prop: "landmark",
      title: "Landmark",
      // isSortable: true,
      isFilterable: true,
      cell: (row) => {
        const landmark = row.shipping_address_details?.landmark || "-";
        return <span>{landmark}</span>;
      },
    },
    {
      prop: "items",
      title: "Items",
      cell: (row) => (
        <div>
          <div className="fw-bold">{row.items?.length || 0} item(s)</div>
          {row.items && row.items.length > 0 && (
            <small className="text-muted">
              {row.items.slice(0, 2).map((item, index) => {
                const productName = item.product_name || item.product?.name || "Unknown Product";
                const unit = item.unit || "piece";

                // Format: Product Name (200g) X 3
                let itemDisplay;
                if (item.order_quantity && item.product_quantity) {
                  // Show: Product Name (200g) X 3
                  const productQty = formatQuantity(item.product_quantity, unit);
                  itemDisplay = `${productName} (${productQty}${unit}) X ${item.order_quantity}`;
                } else {
                  // Fallback for legacy data
                  const qty = formatQuantity(item.quantity || 0, unit);
                  itemDisplay = `${productName} (${qty}${unit})`;
                }

                return (
                  <span key={index}>
                    {itemDisplay}
                    {index < Math.min(row.items.length, 2) - 1 && ", "}
                  </span>
                );
              })}
              {row.items.length > 2 && ` +${row.items.length - 2} more`}
            </small>
          )}
        </div>
      ),
    },
    {
      prop: "final_amount",
      title: isVendor ? "Your Amount" : "Total Amount",
      // isSortable: true,
      cell: (row) => {
        if (isVendor) {
          const vendorAmount = parseFloat(row.vendor_subtotal || 0);
          return (
            <div>
              <span className="fw-bold text-success">
                ₹{vendorAmount.toFixed(2)}
              </span>
              <small className="text-muted d-block" style={{ fontSize: "10px" }}>
                (Your Items)
              </small>
            </div>
          );
        }
        // Use final_amount which includes tax, delivery charge, and discount
        const finalAmount = parseFloat(row.final_amount || 0);
        return (
          <span className="fw-bold text-success">
            ₹{finalAmount.toFixed(2)}
          </span>
        );
      },
    },
    {
      prop: "order_status",
      title: "Order Status",
      // isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Badge bg={getStatusBadgeVariant(row.order_status)}>
          {toTitleCase(row.order_status)}
        </Badge>
      ),
    },
    {
      prop: "return_details",
      title: "Return / Exchange",
      cell: (row) => {
        if (!row.return_details) {
          return <span className="text-muted fw-light">—</span>;
        }

        const isExchange = row.return_details.type === "exchange";
        const status = row.return_details.status;

        // Curated modern status color palette
        let statusStyle = {
          bg: "#fffbeb",
          color: "#b45309",
          border: "#fde68a",
          label: "Pending Review",
        };

        switch (status) {
          case "approved":
            statusStyle = { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", label: "Approved" };
            break;
          case "pickup_scheduled":
            statusStyle = { bg: "#f0f9ff", color: "#0369a1", border: "#bae6fd", label: "Pickup Scheduled" };
            break;
          case "pickup_completed":
            statusStyle = { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", label: "In Transit" };
            break;
          case "received":
            statusStyle = { bg: "#fdf4ff", color: "#a21caf", border: "#f5d0fe", label: "Package Received" };
            break;
          case "replacement_dispatched":
            statusStyle = { bg: "#eef2ff", color: "#4338ca", border: "#c7d2fe", label: "Replacement Dispatched" };
            break;
          case "completed":
            statusStyle = { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0", label: isExchange ? "Exchange Completed" : "Refund Completed" };
            break;
          case "rejected":
            statusStyle = { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", label: "Rejected" };
            break;
          case "cancelled":
            statusStyle = { bg: "#f8fafc", color: "#475569", border: "#e2e8f0", label: "Cancelled" };
            break;
          default:
            statusStyle = { bg: "#fffbeb", color: "#b45309", border: "#fde68a", label: toTitleCase(status) };
        }

        const awbNumber = isExchange ? row.return_details.replacement_awb_number : row.return_details.reverse_awb_number;

        return (
          <div style={{ minWidth: "140px" }}>
            <Link
              to={`/admin/returns/details/${row.return_details.id}`}
              className="text-decoration-none d-inline-block"
              title={`View ${isExchange ? "Exchange" : "Return"} Details`}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 10px",
                  borderRadius: "20px",
                  backgroundColor: statusStyle.bg,
                  border: `1px solid ${statusStyle.border}`,
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 3px 8px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {/* Micro Type Chip */}
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "3px",
                    padding: "1px 6px",
                    borderRadius: "12px",
                    fontSize: "9px",
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    textTransform: "uppercase",
                    backgroundColor: isExchange ? "#3b82f6" : "#f59e0b",
                    color: "#ffffff",
                  }}
                >
                  {isExchange ? <FaExchangeAlt size={7} /> : <FaUndo size={7} />}
                  {isExchange ? "EXC" : "RET"}
                </span>

                {/* Status text */}
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: statusStyle.color,
                    whiteSpace: "nowrap",
                  }}
                >
                  {statusStyle.label}
                </span>
              </div>
            </Link>

            {/* Subtle AWB display if present */}
            {awbNumber && (
              <div
                className="mt-1 font-monospace text-muted d-flex align-items-center"
                style={{ fontSize: "9.5px", letterSpacing: "0.2px" }}
              >
                <span style={{ color: "#94a3b8", marginRight: "3px" }}>AWB:</span>
                <span style={{ color: "#334155", fontWeight: 500 }}>{awbNumber}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      prop: "payment_status",
      title: "Payment Status",
      // isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Badge bg={getPaymentStatusBadgeVariant(row.payment_status)}>
          {toTitleCase(row.payment_status)}
        </Badge>
      ),
    },
    {
      prop: "created_at",
      title: "Order Date",
      // isSortable: true,
      cell: (row) => formatDate(row.created_at || new Date()),
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => {
        const isAccepted = row.order_status === "accepted";
        const isCancelled = row.order_status === "cancelled";
        const isPlaced = row.order_status === "placed";
        const isPaid = isOrderPaid(row);

        return (
          <div className="d-flex gap-2">
            {/* Invoice Preview Button - Only for paid orders */}
            {isPaid && (
              <Button
                size="sm"
                variant="outline-info"
                onClick={() => handleInvoicePreview(row.id)}
                title="Preview Invoice"
              >
                <FaFileInvoice /> Invoice
              </Button>
            )}

            {/* Accept Button */}
            <Button
              size="sm"
              variant="outline-success"
              onClick={() => handleAcceptOrder(row.id, row.order_no || row.id.slice(-8))}
              disabled={!isPlaced || actionLoading[row.id]} // Only enabled when status = placed
              title={isAccepted ? "Order Already Accepted" : "Accept Order"}
            >
              {isAccepted ? "Accepted" : "Accept"}
            </Button>

            {/* Cancel Button */}
            <Button
              size="sm"
              variant="outline-danger-custom"
              onClick={() => handleCancelOrder(row.id, row.order_no || row.id.slice(-8))}
              disabled={isCancelled || actionLoading[row.id]} // Disabled when status = cancelled
              title={isCancelled ? "Order Cancelled" : "Cancel Order"}
            >
              {isCancelled ? "Cancelled" : "Cancel"}
            </Button>
          </div>
        );
      },
    },
    // {
    //   prop: "actions",
    //   title: "Actions",
    //   cell: (row) => (
    //     <div className="d-flex gap-2">
    //       <Button
    //         size="sm"
    //         variant="outline-info"
    //         onClick={() => viewOrderDetails(row.id)}
    //         title="View Details"
    //       >
    //         View
    //       </Button>

    //       {row.order_status === 'placed' && (
    //         <>
    //           <Button
    //             size="sm"
    //             variant="outline-success"
    //             onClick={() => handleAcceptOrder(row.id, row.id.slice(-8))}
    //             disabled={actionLoading[row.id]}
    //             title="Accept Order"
    //           >
    //             Accept
    //           </Button>
    //           <Button
    //             size="sm"
    //             variant="outline-danger"
    //             onClick={() => handleCancelOrder(row.id, row.id.slice(-8))}
    //             disabled={actionLoading[row.id]}
    //             title="Cancel Order"
    //           >
    //             Cancel
    //           </Button>
    //         </>
    //       )}

    //       {row.order_status === 'accepted' && (
    //         <Button
    //           size="sm"
    //           variant="outline-success"
    //           disabled={true}
    //           title="Order Already Accepted"
    //         >
    //           Accepted
    //         </Button>
    //       )}

    //       {row.order_status === 'cancelled' && (
    //         <Button
    //           size="sm"
    //           variant="outline-danger"
    //           disabled={true}
    //           title="Order Cancelled"
    //         >
    //           Cancelled
    //         </Button>
    //       )}

    //       {row.order_status === 'accepted' && (
    //         <Button
    //           size="sm"
    //           variant="outline-primary"
    //           onClick={() => handleStatusUpdate(row.id, 'packed')}
    //           disabled={actionLoading[row.id]}
    //           title="Mark as Packed"
    //         >
    //           Pack
    //         </Button>
    //       )}

    //       {row.order_status === 'packed' && (
    //         <Button
    //           size="sm"
    //           variant="outline-primary"
    //           onClick={() => handleStatusUpdate(row.id, 'out_for_delivery')}
    //           disabled={actionLoading[row.id]}
    //           title="Mark as Out for Delivery"
    //         >
    //           Ship
    //         </Button>
    //       )}

    //       {row.order_status === 'out_for_delivery' && (
    //         <Button
    //           size="sm"
    //           variant="outline-success"
    //           onClick={() => handleStatusUpdate(row.id, 'delivered')}
    //           disabled={actionLoading[row.id]}
    //           title="Mark as Delivered"
    //         >
    //           Deliver
    //         </Button>
    //       )}
    //     </div>
    //   ),
    // },
  ];

  // Filter orders client-side for instant responsiveness (declared before early returns to satisfy React Hook rules)
  const filteredOrders = React.useMemo(() => {
    return orders.filter((order) => {
      let matches = true;
      if (orderStatusFilter && order.order_status !== orderStatusFilter) {
        matches = false;
      }
      if (paymentStatusFilter && order.payment_status !== paymentStatusFilter) {
        matches = false;
      }
      // Return / Exchange filter
      if (returnFilter === "return") {
        if (!order.return_details || order.return_details.type !== "return") {
          matches = false;
        }
      } else if (returnFilter === "exchange") {
        if (!order.return_details || order.return_details.type !== "exchange") {
          matches = false;
        }
      } else if (returnFilter === "has_request") {
        if (!order.return_details) {
          matches = false;
        }
      }

      // Date Range filter
      if (dateRange && dateRange[0] && dateRange[1]) {
        const orderDate = new Date(order.created_at);

        // Start date: start of day (00:00:00.000)
        const startDate = new Date(dateRange[0]);
        startDate.setHours(0, 0, 0, 0);

        // End date: end of day (23:59:59.999)
        const endDate = new Date(dateRange[1]);
        endDate.setHours(23, 59, 59, 999);

        if (orderDate < startDate || orderDate > endDate) {
          matches = false;
        }
      }
      return matches;
    });
  }, [orders, orderStatusFilter, paymentStatusFilter, dateRange, returnFilter]);

  if (loading) {
    return <div className="text-center mt-4">Loading orders...</div>;
  }

  if (error) {
    return <div className="text-center text-danger mt-4">{error}</div>;
  }

  const labels = {
    beforeSelect: "",
  };

  // Calculate dynamic e-commerce stats from filtered orders state (matching Products dashboard behaviour)
  const totalOrdersCount = filteredOrders.length;
  const pendingOrdersCount = filteredOrders.filter(o => ["placed", "accepted", "processing", "packed"].includes(o.order_status)).length;
  const completedOrdersCount = filteredOrders.filter(o => o.order_status === "delivered").length;
  const totalRevenue = filteredOrders
    .filter(o => o.order_status !== "cancelled" && o.order_status !== "returned")
    .reduce((sum, o) => sum + parseFloat((isVendor ? o.vendor_subtotal : o.final_amount) || 0), 0);

  return (
    <div className="page-container">
      <style>{`
        /* Premium Floating Table Card Layout */
        .page-container {
          padding: 30px;
          background-color: #f8fafc;
          min-height: 100vh;
        }

        .compact-controls {
          background-color: #ffffff;
          padding: 20px;
          border-radius: 16px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
          border: 1px solid #f0f0f0;
          margin-bottom: 25px;
        }



        /* Modernized Elegant Status Pills */
        .badge {
          padding: 7px 13px !important;
          border-radius: 30px !important;
          font-weight: 700 !important;
          font-size: 0.72rem !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          display: inline-block !important;
          border: 1px solid transparent !important;
        }

        .bg-warning {
          background-color: #fffbeb !important;
          color: #d97706 !important;
          border: 1px solid #fde68a !important;
        }

        .bg-info {
          background-color: #eff6ff !important;
          color: #1d4ed8 !important;
          border: 1px solid #bfdbfe !important;
        }

        .bg-primary {
          background-color: #f5f3ff !important;
          color: #6d28d9 !important;
          border: 1px solid #ddd6fe !important;
        }

        .bg-success {
          background-color: #ecfdf5 !important;
          color: #047857 !important;
          border: 1px solid #a7f3d0 !important;
        }

        .bg-danger {
          background-color: #fef2f2 !important;
          color: #991b1b !important;
          border: 1px solid #fecaca !important;
        }

        .bg-secondary {
          background-color: #f8fafc !important;
          color: #475569 !important;
          border: 1px solid #cbd5e1 !important;
        }

        /* Premium Stat Cards with gold glows */
        .stat-card {
          border: 1px solid rgba(0, 0, 0, 0.05) !important;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .stat-card:hover {
          transform: translateY(-5px) !important;
          box-shadow: 0 16px 32px rgba(0, 0, 0, 0.08) !important;
        }
        
        .stat-icon-wrapper {
          transition: all 0.3s ease;
        }

        .stat-card:hover .stat-icon-wrapper {
          transform: scale(1.15) rotate(4deg) !important;
        }

        /* Pagination custom style overrides */
        .page-link {
          border: none !important;
          color: #0f172a !important;
          padding: 8px 16px !important;
          border-radius: 8px !important;
          margin: 0 3px !important;
          font-weight: 600 !important;
        }
        
        .page-item.active .page-link {
          background-color: #f6c90e !important;
          color: #000000 !important;
          box-shadow: 0 4px 10px rgba(246, 201, 14, 0.3) !important;
        }

        .breadcrum {
          font-weight: 700 !important;
          color: #000000 !important;
          letter-spacing: -0.2px !important;
        }
        
        /* Modern Table layout cleanups */
        .table-responsive {
          background: transparent !important;
          border: none !important;
        }
      `}</style>
      {/* Delivery Partner Selection Modal */}
      <Modal
        show={showDeliveryModal}
        onHide={cancelDeliverySelection}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Accept Order - Select Delivery Partner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Order Details Section */}
          {orderToAccept && (
            <div className="mb-4">
              <h6 className="fw-bold text-primary mb-3">Order Details</h6>
              <div
                className="row pt-3"
                style={{ backgroundColor: "var(--theme-color2)" }}
              >
                <div className="col-md-6">
                  <p className="texr-left">
                    <strong>Order ID:</strong> #{orderToAccept.id}
                  </p>
                  <p className="texr-left">
                    <strong>Customer Name:</strong>{" "}
                    {orders.find((o) => o.id === orderToAccept.id)
                      ?.shipping_address_details?.receiver_name || "Unknown"}
                  </p>
                  <p className="texr-left">
                    <strong>Customer Phone:</strong>{" "}
                    {orders.find((o) => o.id === orderToAccept.id)
                      ?.customer_phone || "Unknown"}
                  </p>
                  <p className="texr-left">
                    <strong>Total Amount:</strong>{" "}
                    <span className="text-success fw-bold">
                      {formatCurrency(
                        parseFloat(
                          orders.find((o) => o.id === orderToAccept.id)
                            ?.final_amount || 0
                        )
                      )}
                    </span>
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="texr-left">
                    <strong>Items:</strong>{" "}
                    {orders.find((o) => o.id === orderToAccept.id)?.items
                      ?.length || 0}{" "}
                    item(s)
                  </p>
                  <p className="texr-left">
                    <strong>Order Date:</strong>{" "}
                    {formatDate(
                      orders.find((o) => o.id === orderToAccept.id)
                        ?.created_at || new Date()
                    )}
                  </p>
                  <p className="texr-left">
                    <strong>Payment Status:</strong>
                    <Badge
                      bg={getPaymentStatusBadgeVariant(
                        orders.find((o) => o.id === orderToAccept.id)
                          ?.payment_status
                      )}
                      className="ms-2"
                    >
                      {toTitleCase(
                        orders.find((o) => o.id === orderToAccept.id)
                          ?.payment_status || "Unknown"
                      )}
                    </Badge>
                  </p>
                </div>
              </div>

              {/* Order Items Preview */}
              {orders.find((o) => o.id === orderToAccept.id)?.items &&
                orders.find((o) => o.id === orderToAccept.id).items.length >
                0 && (
                  <div className="mt-3">
                    <h6 className="fw-bold">Order Items:</h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>Product</th>
                            <th>Quantity</th>
                            <th>Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders
                            .find((o) => o.id === orderToAccept.id)
                            .items.slice(0, 3)
                            .map((item, index) => {
                              const productName = item.product_name || "Unknown Product";
                              const unit = item.unit || "piece";
                              let quantityDisplay;

                              if (item.order_quantity && item.product_quantity) {
                                const productQty = formatQuantity(item.product_quantity, unit);
                                quantityDisplay = `${productQty}${unit} X ${item.order_quantity}`;
                              } else {
                                quantityDisplay = formatQuantityWithUnit(item.quantity, unit);
                              }

                              return (
                                <tr key={index}>
                                  <td>{productName}</td>
                                  <td>{quantityDisplay}</td>
                                  <td>{formatCurrency(item.subtotal || item.price || 0)}</td>
                                </tr>
                              );
                            })}
                          {orders.find((o) => o.id === orderToAccept.id).items
                            .length > 3 && (
                              <tr>
                                <td
                                  colSpan="3"
                                  className="text-center text-muted"
                                >
                                  +
                                  {orders.find((o) => o.id === orderToAccept.id)
                                    .items.length - 3}{" "}
                                  more items
                                </td>
                              </tr>
                            )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              <hr className="my-3" />
            </div>
          )}

          <p className="fw-bold">
            Please select a delivery partner for this order:
          </p>
          {deliveryPartnersLoading ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Loading delivery partners...</span>
            </div>
          ) : (
            <Form.Group>
              <Form.Label className="fw-semibold">Delivery Partner</Form.Label>
              <Form.Select
                value={selectedDeliveryPartner}
                onChange={(e) => setSelectedDeliveryPartner(e.target.value)}
                required
              >
                <option value="">Select a delivery partner</option>
                <optgroup label="Automated 3PL Couriers">
                  <option value="delhivery">Delhivery (Automated 3PL Courier)</option>
                  <option value="shadowfax">Shadowfax (Automated 3PL Courier)</option>
                  <option value="xpressbees">XpressBees (Automated 3PL Courier)</option>
                </optgroup>
                <optgroup label="Manual / Self Dispatch">
                  <option value="manual">Manual / Self Delivery (Bypass 3PL)</option>
                </optgroup>
              </Form.Select>
              {selectedDeliveryPartner === "manual" && (
                <div className="alert alert-info py-2 px-3 mt-3 mb-0 small text-start d-flex align-items-center gap-2">
                  <i className="fas fa-info-circle text-primary fs-6"></i>
                  <span>
                    <strong>Manual / Self Delivery:</strong> The order will be accepted without generating an automated 3PL shipment. You can assign a rider or enter tracking details manually later.
                  </span>
                </div>
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-flex align-items-center gap-1.5"
              onClick={() => confirmAcceptOrder("manual")}
              disabled={actionLoading[orderToAccept?.id]}
              title="Accept immediately without 3PL courier (Manual/Self Dispatch)"
            >
              <i className="fas fa-forward"></i> Bypass & Accept
            </Button>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="secondary"
              className="btn-outline-danger-custom"
              onClick={cancelDeliverySelection}
              disabled={actionLoading[orderToAccept?.id]}
            >
              Cancel
            </Button>
            <Button
              variant="btn-success"
              className="btn-outline-success"
              onClick={() => confirmAcceptOrder()}
              disabled={
                !selectedDeliveryPartner || actionLoading[orderToAccept?.id]
              }
            >
              {actionLoading[orderToAccept?.id] ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Accepting...
                </>
              ) : (
                "Accept Order"
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Cancellation Reason Modal */}
      <Modal
        show={showCancelReasonModal}
        onHide={cancelCancellationReason}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Order Details Section */}
          {orderToCancel && (
            <div className="mb-4">
              <h6 className="fw-bold text-danger mb-3">Order Details</h6>
              <div
                className="row pt-3"
                style={{ backgroundColor: "var(--theme-color2)" }}
              >
                <div className="col-md-6">
                  <p className="texr-left">
                    <strong>Order ID:</strong> #{orderToCancel.id}
                  </p>
                  <p className="texr-left">
                    <strong>Customer Name:</strong>{" "}
                    {orders.find((o) => o.id === orderToCancel.id)
                      ?.shipping_address_details?.receiver_name || "Unknown"}
                  </p>
                  <p className="texr-left">
                    <strong>Customer Phone:</strong>{" "}
                    {orders.find((o) => o.id === orderToCancel.id)
                      ?.customer_phone || "Unknown"}
                  </p>
                  <p className="texr-left">
                    <strong>Total Amount:</strong>{" "}
                    <span className="text-success fw-bold">
                      {formatCurrency(
                        parseFloat(
                          orders.find((o) => o.id === orderToCancel.id)
                            ?.final_amount || 0
                        )
                      )}
                    </span>
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="texr-left">
                    <strong>Items:</strong>{" "}
                    {orders.find((o) => o.id === orderToCancel.id)?.items
                      ?.length || 0}{" "}
                    item(s)
                  </p>
                  <p className="texr-left">
                    <strong>Order Date:</strong>{" "}
                    {formatDate(
                      orders.find((o) => o.id === orderToCancel.id)
                        ?.created_at || new Date()
                    )}
                  </p>
                  <p className="texr-left">
                    <strong>Payment Status:</strong>
                    <Badge
                      bg={getPaymentStatusBadgeVariant(
                        orders.find((o) => o.id === orderToCancel.id)
                          ?.payment_status
                      )}
                      className="ms-2"
                    >
                      {toTitleCase(
                        orders.find((o) => o.id === orderToCancel.id)
                          ?.payment_status || "Unknown"
                      )}
                    </Badge>
                  </p>
                </div>
              </div>

              {/* Order Items Preview */}
              {orders.find((o) => o.id === orderToCancel.id)?.items &&
                orders.find((o) => o.id === orderToCancel.id).items.length >
                0 && (
                  <div className="mt-3">
                    <h6 className="fw-bold">Order Items:</h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>Product</th>
                            <th>Quantity</th>
                            <th>Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders
                            .find((o) => o.id === orderToCancel.id)
                            .items.slice(0, 3)
                            .map((item, index) => {
                              const productName = item.product_name || "Unknown Product";
                              const unit = item.unit || "piece";
                              let quantityDisplay;

                              if (item.order_quantity && item.product_quantity) {
                                const productQty = formatQuantity(item.product_quantity, unit);
                                quantityDisplay = `${productQty}${unit} X ${item.order_quantity}`;
                              } else {
                                quantityDisplay = formatQuantityWithUnit(item.quantity, unit);
                              }

                              return (
                                <tr key={index}>
                                  <td>{productName}</td>
                                  <td>{quantityDisplay}</td>
                                  <td>{formatCurrency(item.subtotal || item.price || 0)}</td>
                                </tr>
                              );
                            })}
                          {orders.find((o) => o.id === orderToCancel.id).items
                            .length > 3 && (
                              <tr>
                                <td
                                  colSpan="3"
                                  className="text-center text-muted"
                                >
                                  +
                                  {orders.find((o) => o.id === orderToCancel.id)
                                    .items.length - 3}{" "}
                                  more items
                                </td>
                              </tr>
                            )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              <hr className="my-3" />
            </div>
          )}

          <p className="fw-bold text-danger">
            Please provide a reason for cancelling this order:
          </p>
          <Form.Group>
            <Form.Label>Reason for Cancellation</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder="Enter the reason for cancelling this order..."
              required
            />
            <Form.Text className="text-muted">
              This reason will be recorded and may be shared with the customer.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className="btn-outline-success"
            onClick={cancelCancellationReason}
          >
            Keep Order
          </Button>
          <Button
            variant="danger"
            className="btn-outline-danger-custom"
            onClick={confirmCancelOrder}
            disabled={
              !cancellationReason.trim() || actionLoading[orderToCancel?.id]
            }
          >
            {actionLoading[orderToCancel?.id] ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Cancelling...
              </>
            ) : (
              "Cancel Order"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={showConfirmModal}
        onHide={cancelDeleteOrder}
        onConfirm={confirmCancelOrder}
        title="Cancel Order"
        message={`Are you sure you want to cancel order #${orderToCancel?.id}? This action cannot be undone.`}
        confirmText="Cancel Order"
        cancelText="Keep Order"
        variant="danger"
        isLoading={orderToCancel && actionLoading[orderToCancel.id]}
      />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Orders
        </Link>
        {isVendor && (
          <Badge bg="info" className="px-3 py-2 text-dark fs-7 fw-semibold shadow-sm">
            <i className="fas fa-store me-1.5"></i> Vendor Portal View
          </Badge>
        )}
      </div>

      {/* Admin Stats Dashboard (Product-List UI Style) */}
      <div className="row g-3 mb-4 mt-1">
        <div className="col-md-3">
          <div className="admin-stats-card p-3 shadow-sm border rounded bg-white d-flex align-items-center">
            <div className="stats-icon-wrapper bg-primary-soft text-primary me-3">
              <FaShoppingBag size={20} />
            </div>
            <div>
              <div className="stats-label text-muted small fw-semibold">Total Orders</div>
              <div className="stats-value h4 mb-0 fw-bold">{totalOrdersCount}</div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="admin-stats-card p-3 shadow-sm border rounded bg-white d-flex align-items-center">
            <div className="stats-icon-wrapper bg-warning-soft text-warning me-3">
              <FaHourglassHalf size={20} />
            </div>
            <div>
              <div className="stats-label text-muted small fw-semibold">Pending Orders</div>
              <div className="stats-value h4 mb-0 fw-bold">{pendingOrdersCount}</div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="admin-stats-card p-3 shadow-sm border rounded bg-white d-flex align-items-center">
            <div className="stats-icon-wrapper bg-success-soft text-success me-3">
              <FaCheckCircle size={20} />
            </div>
            <div>
              <div className="stats-label text-muted small fw-semibold">Delivered</div>
              <div className="stats-value h4 mb-0 fw-bold">{completedOrdersCount}</div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="admin-stats-card p-3 shadow-sm border rounded bg-white d-flex align-items-center">
            <div className="stats-icon-wrapper bg-info-soft text-info me-3">
              <FaCoins size={20} />
            </div>
            <div>
              <div className="stats-label text-muted small fw-semibold">
                {isVendor ? "Your Sales Volume" : "Sales Volume"}
              </div>
              <div className="stats-value h4 mb-0 fw-bold">₹{totalRevenue.toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>

      <DatatableWrapper
        body={filteredOrders}
        headers={headers}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 10, options: [5, 10, 15, 20] },
        }}
      >
        <div className="orders-compact-toolbar d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 bg-light rounded border">
          <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
            {/* Search Filter */}
            <div style={{ flex: "1 1 150px", minWidth: "140px", maxWidth: "200px" }}>
              <Filter
                placeholder="Search orders..."
                className="form-control-sm"
              />
            </div>

            {/* Order Status Filter */}
            <div style={{ flex: "0 1 125px", minWidth: "115px" }}>
              <Form.Select
                value={orderStatusFilter}
                onChange={(e) => setOrderStatusFilter(e.target.value)}
                size="sm"
                className="form-select-sm shadow-none"
              >
                <option value="">All Statuses</option>
                <option value="placed">Placed</option>
                <option value="accepted">Accepted</option>
                <option value="packed">Packed</option>
                <option value="out_for_delivery">Out For Delivery</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
                <option value="returned">Returned</option>
              </Form.Select>
            </div>

            {/* Return / Exchange Filter */}
            <div style={{ flex: "0 1 145px", minWidth: "130px" }}>
              <Form.Select
                value={returnFilter}
                onChange={(e) => setReturnFilter(e.target.value)}
                size="sm"
                className="form-select-sm shadow-none"
              >
                <option value="">All (Return/Exch)</option>
                <option value="has_request">Has Return/Exch</option>
                <option value="return">↩️ Returns Only</option>
                <option value="exchange">🔄 Exchanges Only</option>
              </Form.Select>
            </div>

            {/* Payment Status Filter */}
            <div style={{ flex: "0 1 120px", minWidth: "110px" }}>
              <Form.Select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                size="sm"
                className="form-select-sm shadow-none"
              >
                <option value="">All Payments</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </div>

            {/* Date Range Filter */}
            <div style={{ flex: "0 1 195px", minWidth: "185px" }} className="rsuite-daterange-picker-wrapper">
              <DateRangePicker
                value={dateRange}
                onChange={(range) => setDateRange(range)}
                character=" – "
                placeholder="Date Range"
                size="sm"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <div className="d-flex align-items-center">
              <PaginationOptions labels={labels} />
            </div>
            <div className="d-flex align-items-center bg-white border rounded px-2 shadow-sm" style={{ height: "33px", fontSize: "12px" }}>
              <span className="text-muted fw-semibold me-2 text-nowrap">Total Orders:</span>
              <span className="badge bg-dark px-2 py-1 text-white rounded" style={{ fontSize: "11px", fontWeight: "bold" }}>
                {filteredOrders?.length || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className="table-responsive shadow-sm rounded bg-white"
          style={{ position: "relative" }}
        >
          <table className="table table-striped table-bordered align-middle mb-0">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>
    </div>
  );
};

export default AdminOrders;
